if (!customElements.get('product-viewer-video')) {
  class ProductViewerVideo extends HTMLElement {
    constructor() {
      super();
      this.list = this.querySelector('.pvv__list');
      this.items = Array.from(this.querySelectorAll('.pvv__item'));
      this.medias = Array.from(this.querySelectorAll('.pvv__media[data-index]'));
      this.defaultMedia = this.querySelector('.pvv__media--default');
      this.openFirst = this.dataset.openFirst === 'true';

      this.current = -1;
      this.activeMedia = this.defaultMedia;

      this.onVisibility = this.onVisibility.bind(this);
      this._onResize = this._onResize.bind(this);
      this._lastVariant = null;
    }

    connectedCallback() {
      if (!this.items.length) return;

      this.items.forEach((item, index) => {
        const header = item.querySelector('.pvv__item-header');
        if (header) {
          header.addEventListener('click', () => {
            if (item.classList.contains('is-active')) {
              this.close();
              return;
            }
            this.open(index);
          });
        }

        const prevArrow = item.querySelector('.pvv__item-arrow--prev');
        const nextArrow = item.querySelector('.pvv__item-arrow--next');
        if (prevArrow) {
          prevArrow.addEventListener('click', (e) => {
            e.stopPropagation();
            this.open(index - 1);
          });
        }
        if (nextArrow) {
          nextArrow.addEventListener('click', (e) => {
            e.stopPropagation();
            this.open(index + 1);
          });
        }
      });

      document.addEventListener('visibilitychange', this.onVisibility);
      window.addEventListener('resize', this._onResize);

      this._lastVariant = this._getBreakpointVariant();

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.resumeActiveVideo();
              else this.pauseAllVideos();
            });
          },
          { threshold: 0.25 }
        );
        this.observer.observe(this);
      }

      if (this.openFirst && this.items.length) {
        requestAnimationFrame(() => this.open(0, true));
      } else {
        this.closeImmediate();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('resize', this._onResize);
      if (this.observer) this.observer.disconnect();
    }

    _onResize() {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        if (this.current < 0) return;
        const variant = this._getBreakpointVariant();
        const variantChanged = this._lastVariant !== null && this._lastVariant !== variant;
        this._lastVariant = variant;
        if (variantChanged) this.playActiveVideo(true);
      }, 150);
    }

    _getBreakpointVariant() {
      const desktop = window.matchMedia('(min-width: 990px)').matches;
      const tablet = window.matchMedia('(min-width: 750px)').matches;
      if (desktop) return 'desktop';
      if (tablet) return 'tablet';
      return 'mobile';
    }

    _getVisibleVideo(media) {
      if (!media) return null;
      return media.querySelector(`.pvv__media-source--${this._getBreakpointVariant()} video`);
    }

    open(index, initial = false) {
      if (index < 0 || index >= this.items.length) return;

      const switching = !initial && this.current !== -1 && this.current !== index;
      const wasClosed = this.current === -1;

      this.current = index;
      this.classList.add('is-open');
      this.list?.classList.add('has-active');

      this.items.forEach((item, i) => {
        const active = i === index;
        item.classList.toggle('is-active', active);
        const header = item.querySelector('.pvv__item-header');
        if (header) header.setAttribute('aria-expanded', active ? 'true' : 'false');
      });

      const isFirst = index === 0;
      const isLast = index === this.items.length - 1;
      const activeItem = this.items[index];
      const prevArrow = activeItem?.querySelector('.pvv__item-arrow--prev');
      const nextArrow = activeItem?.querySelector('.pvv__item-arrow--next');
      if (prevArrow) prevArrow.disabled = isFirst;
      if (nextArrow) nextArrow.disabled = isLast;

      let mode = 'switch';
      if (switching) {
        mode = 'switch';
      } else if (wasClosed || this.activeMedia === this.defaultMedia) {
        mode = 'open';
      }

      this.switchMedia(this.medias[index], mode);
    }

    close() {
      this.closeImmediate();
    }

    closeImmediate() {
      this.current = -1;
      this.classList.remove('is-open');
      this.list?.classList.remove('has-active');

      this.items.forEach((item) => {
        item.classList.remove('is-active');
        const header = item.querySelector('.pvv__item-header');
        if (header) header.setAttribute('aria-expanded', 'false');
      });

      this.switchMedia(this.defaultMedia, 'close');
    }

    _prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    _resetMediaClasses(el) {
      if (!el) return;
      el.classList.remove(
        'is-active',
        'is-leaving',
        'is-entering',
        'is-fading',
        'is-leaving--switch',
        'is-leaving--zoom',
        'is-leaving--close',
        'is-leaving--default-out',
        'is-entering--switch',
        'is-entering--open',
        'is-entering--close'
      );
      clearTimeout(el._leaveTimer);
      clearTimeout(el._enterTimer);
    }

    _hideMedia(el) {
      if (!el) return;
      el.style.visibility = 'hidden';
      el.style.display = 'none';
      el.style.pointerEvents = '';
    }

    _showMedia(el, pointerEvents = 'auto') {
      if (!el) return;
      el.style.visibility = 'visible';
      el.style.display = 'block';
      el.style.pointerEvents = pointerEvents;
    }

    _finalizeMediaEnter(el) {
      if (!el) return;
      el.classList.remove('is-entering', 'is-entering--switch', 'is-entering--open', 'is-entering--close');
      el.classList.add('is-active');
      el.style.pointerEvents = 'auto';
    }

    _scheduleLeaveCleanup(el, ms) {
      clearTimeout(el._leaveTimer);
      el._leaveTimer = setTimeout(() => {
        el.classList.remove(
          'is-leaving',
          'is-leaving--switch',
          'is-leaving--zoom',
          'is-leaving--close',
          'is-leaving--default-out',
          'is-fading'
        );
        this._hideMedia(el);
      }, ms);
    }

    _scheduleEnterFinalize(el, ms) {
      clearTimeout(el._enterTimer);
      el._enterTimer = setTimeout(() => this._finalizeMediaEnter(el), ms);
    }

    _prepareMediaLeave(el, leaveVariant, durationMs) {
      el.classList.remove('is-entering', 'is-entering--switch', 'is-entering--open', 'is-entering--close');
      clearTimeout(el._leaveTimer);
      clearTimeout(el._enterTimer);
      this._showMedia(el, 'none');
      el.classList.add('is-leaving', leaveVariant);
      el.classList.remove('is-active');
      void el.offsetWidth;
      this._scheduleLeaveCleanup(el, durationMs);
    }

    _prepareMediaEnter(el, enterVariant, durationMs) {
      el.classList.remove(
        'is-active',
        'is-leaving',
        'is-leaving--switch',
        'is-leaving--zoom',
        'is-leaving--close',
        'is-leaving--default-out',
        'is-fading'
      );
      clearTimeout(el._leaveTimer);
      clearTimeout(el._enterTimer);
      this._showMedia(el);
      el.classList.add('is-entering', enterVariant);
      void el.offsetWidth;
      this._scheduleEnterFinalize(el, durationMs);
    }

    switchMedia(toEl, mode = 'switch') {
      if (!toEl || toEl === this.activeMedia) return;
      const fromEl = this.activeMedia;
      const reduced = this._prefersReducedMotion();

      if (fromEl) {
        this.pauseVideo(fromEl);

        if (reduced) {
          this._resetMediaClasses(fromEl);
          this._hideMedia(fromEl);
        } else if (mode === 'switch') {
          this._prepareMediaLeave(fromEl, 'is-leaving--switch', 350);
        } else if (mode === 'close') {
          this._prepareMediaLeave(fromEl, 'is-leaving--zoom', 350);
        } else if (fromEl === this.defaultMedia) {
          this._prepareMediaLeave(fromEl, 'is-leaving--default-out', 350);
        } else {
          this._resetMediaClasses(fromEl);
          this._showMedia(fromEl, 'none');
          fromEl.classList.add('is-fading');
          this._scheduleLeaveCleanup(fromEl, 900);
        }
      }

      if (reduced) {
        this._resetMediaClasses(toEl);
        this._showMedia(toEl);
        toEl.classList.add('is-active');
      } else if (mode === 'switch') {
        this._prepareMediaEnter(toEl, 'is-entering--switch', 550);
      } else if (mode === 'open') {
        this._prepareMediaEnter(toEl, 'is-entering--open', 550);
      } else if (mode === 'close') {
        this._prepareMediaEnter(toEl, 'is-entering--close', 550);
      } else {
        this._resetMediaClasses(toEl);
        this._showMedia(toEl);
        toEl.classList.add('is-active');
      }

      this.playVideo(toEl, toEl.hasAttribute('data-index'));
      this.activeMedia = toEl;
    }

    playVideo(media, restart = false) {
      if (!media) return;
      media.querySelectorAll('video').forEach((video) => {
        video.loop = false;
        video.pause();
      });
      const video = this._getVisibleVideo(media);
      if (!video) return;
      video.loop = false;
      if (restart) video.currentTime = 0;
      video.play().catch(() => {});
    }

    pauseVideo(media) {
      media?.querySelectorAll('video').forEach((video) => video.pause());
    }

    playActiveVideo(restart = false) {
      this.playVideo(this.activeMedia, restart);
    }

    resumeActiveVideo() {
      if (!this.activeMedia) return;
      const video = this._getVisibleVideo(this.activeMedia);
      if (!video || video.ended) return;
      video.loop = false;
      if (video.paused) video.play().catch(() => {});
    }

    pauseAllVideos() {
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    onVisibility() {
      if (document.hidden) this.pauseAllVideos();
      else this.resumeActiveVideo();
    }
  }

  customElements.define('product-viewer-video', ProductViewerVideo);
}
